import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Upload, X, Check } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

/**
 * PlanSponsorIntake Component
 * 
 * A dedicated intake form for Plan Sponsors who are referred to the site
 * (either from a plan participant or their own inquiry).
 * 
 * Styled to match PlanAnalysisScroll.jsx with blue color scheme.
 */
const PlanSponsorIntake = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    email: '',
    company: '',
    phone: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Sponsor-specific fields
    sponsorRole: '',
    numEmployees: '',
    planAssets: '',
    
    // Current Situation
    currentProvider: '',
    renewalDate: '',
    timeline: '',
    painPoints: [],
    otherPainPoint: '',
    
    // Provider Introduction
    wantsIntroduction: null,
    preferredContact: 'email',
    bestTimeToCall: '',
    
    // Consent
    consentContact: false,
    consentSharing: false,
    consentTerms: false,
    consentDisclosure: false,
    
    // Additional
    additionalComments: '',
  });
  
  // Enhanced upload state for multiple document types
  const [uploadedDocuments, setUploadedDocuments] = useState({
    spd: [],
    bpd: [],
    adoption_agreement: []
  });
  const [extractedData, setExtractedData] = useState({
    spd: null,
    bpd: null,
    adoption_agreement: null
  });
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [currentExtracting, setCurrentExtracting] = useState(null);
  
  // UI state
  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const currentProviders = [
    'Fidelity', 'Vanguard', 'Schwab', 'T. Rowe Price', 'TIAA',
    'Principal', 'Empower', 'ADP', 'Paychex', 'John Hancock',
    'MassMutual', 'Nationwide', 'Transamerica', 'Other'
  ];

  const painPointOptions = [
    { id: 'high_fees', label: 'High administrative fees' },
    { id: 'poor_returns', label: 'Poor investment returns' },
    { id: 'limited_options', label: 'Limited investment options' },
    { id: 'poor_service', label: 'Poor participant service' },
    { id: 'compliance', label: 'Compliance concerns' },
    { id: 'recordkeeping', label: 'Difficult recordkeeping' },
    { id: 'other', label: 'Other' },
  ];

  // Styles matching PlanAnalysisScroll.jsx
  const styles = {
    // Colors
    primaryBlue: '#1e3a8a',
    primaryBlueHover: '#1e40af',
    
    // Input styles
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '1rem',
      outline: 'none',
      transition: 'border-color 0.2s',
      boxSizing: 'border-box'
    },
    inputError: {
      borderColor: '#ef4444'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
      color: '#374151'
    },
    
    // Card/Section styles
    section: {
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '2rem',
      marginBottom: '2rem'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1.5rem'
    },
    sectionIcon: {
      width: '40px',
      height: '40px',
      background: 'rgba(30, 58, 138, 0.1)',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0
    },
    sectionSubtitle: {
      fontSize: '0.9375rem',
      color: '#6b7280',
      margin: 0
    },
    
    // Button styles
    primaryButton: {
      background: '#1e3a8a',
      color: 'white',
      border: 'none',
      padding: '0.875rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.2s',
      width: '100%'
    },
    secondaryButton: {
      background: '#f3f4f6',
      color: '#374151',
      border: 'none',
      padding: '0.875rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.2s'
    }
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle pain points (multi-select)
  const handlePainPointToggle = (pointId) => {
    setFormData(prev => {
      const painPoints = prev.painPoints.includes(pointId)
        ? prev.painPoints.filter(p => p !== pointId)
        : [...prev.painPoints, pointId];
      return { ...prev, painPoints };
    });
  };

  // Enhanced file upload handler for multiple document types
  const handleFileUpload = async (event, documentType) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    setUploading(true);
    
    try {
      const validFiles = files.filter(file => {
        const isValidSize = file.size <= 10 * 1024 * 1024;
        const isValidType = file.type === 'application/pdf';
        return isValidSize && isValidType;
      });
      
      if (validFiles.length !== files.length) {
        alert('Some files were rejected. Only PDF files under 10MB are allowed.');
      }
      
      if (validFiles.length === 0) {
        setUploading(false);
        return;
      }
      
      for (const file of validFiles) {
        const fileInfo = {
          id: Math.random().toString(36),
          file: file,
          name: file.name,
          size: file.size,
          type: documentType,
          uploadedAt: new Date(),
        };
        
        setUploadedDocuments(prev => ({
          ...prev,
          [documentType]: [...prev[documentType], fileInfo]
        }));
        
        await extractDocumentData(fileInfo, documentType);
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Extract data from uploaded document
  const extractDocumentData = async (fileInfo, documentType) => {
    setExtracting(true);
    setCurrentExtracting(`${documentType.toUpperCase()}: ${fileInfo.name}`);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', fileInfo.file);
      
      const endpointMap = {
        'spd': '/extract-spd',
        'bpd': '/extract-bpd',
        'adoption_agreement': '/extract-adoption-agreement'
      };
      
      const endpoint = endpointMap[documentType];
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (data.success && data.extracted_data) {
        setExtractedData(prev => ({
          ...prev,
          [documentType]: data.extracted_data
        }));
        
        if (documentType === 'adoption_agreement' || documentType === 'spd') {
          const extractedInfo = data.extracted_data;
          
          if (extractedInfo.employer_name || extractedInfo.employer_name_legal) {
            const employerName = extractedInfo.employer_name || extractedInfo.employer_name_legal;
            if (!formData.company) {
              handleInputChange('company', employerName);
            }
          }
        }
        
        alert(`${documentType.toUpperCase()} extracted successfully!`);
      } else {
        alert(`Extraction failed: ${data.error || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Extraction error:', error);
      alert(`Error extracting ${documentType}. Please try again.`);
    } finally {
      setExtracting(false);
      setCurrentExtracting(null);
    }
  };

  // Remove uploaded document
  const removeDocument = (documentType, fileId) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [documentType]: prev[documentType].filter(f => f.id !== fileId)
    }));
    
    if (uploadedDocuments[documentType].length === 1) {
      setExtractedData(prev => ({
        ...prev,
        [documentType]: null
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formData.sponsorRole) newErrors.sponsorRole = 'Role is required';
    
    if (!formData.currentProvider) {
      newErrors.currentProvider = 'Please select your current provider';
    }
    
    if (!formData.consentContact) newErrors.consentContact = 'Required';
    if (!formData.consentSharing) newErrors.consentSharing = 'Required';
    if (!formData.consentTerms) newErrors.consentTerms = 'Required';
    if (!formData.consentDisclosure) newErrors.consentDisclosure = 'Required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert('Please fix the errors in the form');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const submissionData = {
        ...formData,
        userType: 'sponsor',
        uploaded_documents: {
          spd: uploadedDocuments.spd.map(f => ({ name: f.name, size: f.size, uploadedAt: f.uploadedAt })),
          bpd: uploadedDocuments.bpd.map(f => ({ name: f.name, size: f.size, uploadedAt: f.uploadedAt })),
          adoption_agreement: uploadedDocuments.adoption_agreement.map(f => ({ name: f.name, size: f.size, uploadedAt: f.uploadedAt }))
        },
        extracted_data: extractedData,
        submitted_at: new Date().toISOString(),
      };
      
      const response = await fetch(`${API_BASE_URL}/submit-sponsor-intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Submission failed');
      }
      
      setSubmitSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Submit error:', error);
      alert('Error submitting form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success message
  if (submitSuccess) {
    return (
      <div style={{ background: '#fafbfc', minHeight: '100vh' }}>
        {/* Header */}
        <header style={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1rem 0'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2.5rem' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em' }}>
              Love Your 401k
            </div>
          </div>
        </header>

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '4rem 2.5rem' }}>
          <div style={{
            background: '#ecfdf5',
            border: '2px solid #10b981',
            borderRadius: '12px',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Check size={48} color="white" strokeWidth={3} />
            </div>
            
            <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
              Thank You! Your Request Has Been Submitted
            </h2>
            
            <p style={{ fontSize: '1.125rem', color: '#374151', marginBottom: '2rem' }}>
              Here's what happens next:
            </p>
            
            <div style={{ textAlign: 'left', maxWidth: '500px', margin: '0 auto 2rem' }}>
              {[
                { num: 1, title: 'Confirmation Email', desc: 'Check your email for a confirmation of your submission' },
                { num: 2, title: 'Document Review (24-48 hours)', desc: "We'll analyze any uploaded documents and extract key plan details" },
                ...(formData.wantsIntroduction ? [{ num: 3, title: 'Provider Introductions', desc: "We'll reach out to discuss potential service provider options that could benefit your plan" }] : [])
              ].map((step) => (
                <div key={step.num} style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    background: '#1e3a8a',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ color: 'white', fontWeight: 700 }}>{step.num}</span>
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 700, color: '#111827', margin: '0 0 0.25rem' }}>{step.title}</h4>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '0.9375rem' }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => navigate('/')}
              style={{
                ...styles.primaryButton,
                width: 'auto',
                padding: '0.875rem 2rem'
              }}
              onMouseEnter={(e) => e.target.style.background = styles.primaryBlueHover}
              onMouseLeave={(e) => e.target.style.background = styles.primaryBlue}
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div style={{ background: '#fafbfc', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 2.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1e3a8a', letterSpacing: '0.02em' }}>
              Love Your 401k
            </div>
            <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center' }}>
              <a href="/" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>Search Plans</a>
              <a href="/estimator" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>What Should I Pay?</a>
              <a href="/faq" style={{ color: '#6b7280', textDecoration: 'none', fontSize: '0.9375rem' }}>FAQ</a>
            </div>
          </div>
        </div>
      </header>

      {/* Page Title */}
      <div style={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '2rem 0' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 2.5rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#111827', marginBottom: '0.75rem' }}>
            Plan Sponsor Inquiry
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6b7280', margin: 0 }}>
            Welcome! Please tell us about your company and your 401(k) plan so we can better assist you.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem 2.5rem' }}>
        <form onSubmit={handleSubmit}>
          
          {/* Section 1: Your Information */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIcon}>
                <svg style={{ width: '24px', height: '24px', color: '#1e3a8a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h2 style={styles.sectionTitle}>Your Information</h2>
                <p style={styles.sectionSubtitle}>Help us understand your situation</p>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              {/* Name */}
              <div>
                <label style={styles.label}>
                  Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.name ? styles.inputError : {})
                  }}
                  placeholder="John Smith"
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = errors.name ? '#ef4444' : '#e5e7eb'}
                />
                {errors.name && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{errors.name}</p>}
              </div>
              
              {/* Email */}
              <div>
                <label style={styles.label}>
                  Email <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.email ? styles.inputError : {})
                  }}
                  placeholder="john@company.com"
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = errors.email ? '#ef4444' : '#e5e7eb'}
                />
                {errors.email && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{errors.email}</p>}
              </div>
              
              {/* Company */}
              <div>
                <label style={styles.label}>Company Name</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  style={styles.input}
                  placeholder="Acme Corp"
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              
              {/* Phone */}
              <div>
                <label style={styles.label}>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  style={styles.input}
                  placeholder="(555) 123-4567"
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              
              {/* City */}
              <div>
                <label style={styles.label}>City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  style={styles.input}
                  placeholder="San Francisco"
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              
              {/* State & Zip */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={styles.label}>State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    style={styles.input}
                    placeholder="CA"
                    maxLength="2"
                    onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <div>
                  <label style={styles.label}>Zip</label>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                    style={styles.input}
                    placeholder="94102"
                    maxLength="5"
                    onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>
            </div>
            
            {/* Sponsor Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
              <div>
                <label style={styles.label}>
                  Your Role <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={formData.sponsorRole}
                  onChange={(e) => handleInputChange('sponsorRole', e.target.value)}
                  style={{
                    ...styles.input,
                    ...(errors.sponsorRole ? styles.inputError : {})
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = errors.sponsorRole ? '#ef4444' : '#e5e7eb'}
                >
                  <option value="">Select...</option>
                  <option value="owner">Owner/CEO</option>
                  <option value="cfo">CFO</option>
                  <option value="hr_manager">HR Manager</option>
                  <option value="benefits_admin">Benefits Administrator</option>
                  <option value="advisor">Financial Advisor</option>
                  <option value="other">Other</option>
                </select>
                {errors.sponsorRole && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{errors.sponsorRole}</p>}
              </div>
              
              <div>
                <label style={styles.label}># of Employees</label>
                <select
                  value={formData.numEmployees}
                  onChange={(e) => handleInputChange('numEmployees', e.target.value)}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Select...</option>
                  <option value="1-10">1-10</option>
                  <option value="11-25">11-25</option>
                  <option value="26-50">26-50</option>
                  <option value="51-100">51-100</option>
                  <option value="101-250">101-250</option>
                  <option value="251-500">251-500</option>
                  <option value="501+">501+</option>
                </select>
              </div>
              
              <div>
                <label style={styles.label}>Approx. Plan Assets</label>
                <select
                  value={formData.planAssets}
                  onChange={(e) => handleInputChange('planAssets', e.target.value)}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  <option value="">Select...</option>
                  <option value="under_250k">Under $250,000</option>
                  <option value="250k_500k">$250,000 - $500,000</option>
                  <option value="500k_1m">$500,000 - $1 million</option>
                  <option value="1m_5m">$1 million - $5 million</option>
                  <option value="5m_10m">$5 million - $10 million</option>
                  <option value="10m_25m">$10 million - $25 million</option>
                  <option value="25m_50m">$25 million - $50 million</option>
                  <option value="50m_100m">$50 million - $100 million</option>
                  <option value="over_100m">Over $100 million</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Upload Plan Documents */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIcon}>
                <svg style={{ width: '24px', height: '24px', color: '#1e3a8a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 style={styles.sectionTitle}>Upload Plan Documents</h2>
                <p style={styles.sectionSubtitle}>Help us better understand your plan (optional but recommended)</p>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
              {/* SPD Upload */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem' }}>
                <h4 style={{ fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>Summary Plan Description (SPD)</h4>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Participant-facing document describing plan features
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e, 'spd')}
                  style={{ display: 'none' }}
                  id="spd-upload"
                  multiple
                />
                <label
                  htmlFor="spd-upload"
                  style={{
                    display: 'block',
                    padding: '0.75rem',
                    background: 'rgba(30, 58, 138, 0.1)',
                    color: '#1e3a8a',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(30, 58, 138, 0.15)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(30, 58, 138, 0.1)'}
                >
                  📄 Upload SPD
                </label>
                
                {uploadedDocuments.spd.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    {uploadedDocuments.spd.map(file => (
                      <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', background: '#f9fafb', borderRadius: '6px', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDocument('spd', file.id)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {extractedData.spd && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857', marginBottom: '0.25rem' }}>✓ Extracted Data:</div>
                    <div style={{ fontSize: '0.75rem', color: '#065f46' }}>
                      Plan: {extractedData.spd.plan_name}
                    </div>
                  </div>
                )}
              </div>

              {/* BPD Upload */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem' }}>
                <h4 style={{ fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>Basic Plan Document (BPD)</h4>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Prototype/template document defining plan structure
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e, 'bpd')}
                  style={{ display: 'none' }}
                  id="bpd-upload"
                  multiple
                />
                <label
                  htmlFor="bpd-upload"
                  style={{
                    display: 'block',
                    padding: '0.75rem',
                    background: 'rgba(30, 58, 138, 0.1)',
                    color: '#1e3a8a',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(30, 58, 138, 0.15)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(30, 58, 138, 0.1)'}
                >
                  📘 Upload BPD
                </label>
                
                {uploadedDocuments.bpd.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    {uploadedDocuments.bpd.map(file => (
                      <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', background: '#f9fafb', borderRadius: '6px', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDocument('bpd', file.id)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {extractedData.bpd && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857', marginBottom: '0.25rem' }}>✓ Extracted Data:</div>
                    <div style={{ fontSize: '0.75rem', color: '#065f46' }}>
                      Provider: {extractedData.bpd.provider_name}
                    </div>
                  </div>
                )}
              </div>

              {/* AA Upload */}
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1.5rem' }}>
                <h4 style={{ fontWeight: 600, color: '#111827', marginBottom: '0.5rem' }}>Adoption Agreement (AA)</h4>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Specific elections and plan provisions
                </p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileUpload(e, 'adoption_agreement')}
                  style={{ display: 'none' }}
                  id="aa-upload"
                  multiple
                />
                <label
                  htmlFor="aa-upload"
                  style={{
                    display: 'block',
                    padding: '0.75rem',
                    background: 'rgba(30, 58, 138, 0.1)',
                    color: '#1e3a8a',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(30, 58, 138, 0.15)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(30, 58, 138, 0.1)'}
                >
                  📋 Upload AA
                </label>
                
                {uploadedDocuments.adoption_agreement.length > 0 && (
                  <div style={{ marginTop: '1rem' }}>
                    {uploadedDocuments.adoption_agreement.map(file => (
                      <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', background: '#f9fafb', borderRadius: '6px', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDocument('adoption_agreement', file.id)}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {extractedData.adoption_agreement && (
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '6px' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857', marginBottom: '0.25rem' }}>✓ Extracted Data:</div>
                    <div style={{ fontSize: '0.75rem', color: '#065f46' }}>
                      Plan: {extractedData.adoption_agreement.plan_name}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {extracting && (
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid #3b82f6',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ color: '#1e40af', fontWeight: 600 }}>Extracting data from {currentExtracting}...</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
          </div>

          {/* Section 3: Current Situation */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIcon}>
                <svg style={{ width: '24px', height: '24px', color: '#1e3a8a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div>
                <h2 style={styles.sectionTitle}>Current Situation</h2>
                <p style={styles.sectionSubtitle}>Tell us about your current plan</p>
              </div>
            </div>
            
            {/* Current Provider */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={styles.label}>
                Current Provider <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={formData.currentProvider}
                onChange={(e) => handleInputChange('currentProvider', e.target.value)}
                style={{
                  ...styles.input,
                  maxWidth: '400px',
                  ...(errors.currentProvider ? styles.inputError : {})
                }}
                onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                onBlur={(e) => e.target.style.borderColor = errors.currentProvider ? '#ef4444' : '#e5e7eb'}
              >
                <option value="">Select your current provider...</option>
                {currentProviders.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
              {errors.currentProvider && <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: '0.25rem 0 0' }}>{errors.currentProvider}</p>}
            </div>
            
            {/* Contract Renewal Date */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={styles.label}>Contract Renewal Date (if known)</label>
              <input
                type="date"
                value={formData.renewalDate}
                onChange={(e) => handleInputChange('renewalDate', e.target.value)}
                style={{ ...styles.input, maxWidth: '250px' }}
                onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
              <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.25rem' }}>Helps us time our recommendations</p>
            </div>
            
            {/* Timeline */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={styles.label}>What's your timeline for making a change?</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[
                  { value: 'immediate', label: 'Immediately (0-3 months)', description: 'Ready to switch now' },
                  { value: 'medium', label: 'Medium term (3-6 months)', description: 'Planning ahead' },
                  { value: 'long', label: 'Long term (6-12+ months)', description: 'Researching options' },
                  { value: 'researching', label: 'Just researching', description: 'Gathering information' },
                ].map(option => (
                  <label 
                    key={option.value} 
                    style={{
                      display: 'block',
                      padding: '1rem 1.25rem',
                      border: `2px solid ${formData.timeline === option.value ? '#1e3a8a' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: formData.timeline === option.value ? 'rgba(30, 58, 138, 0.05)' : 'white',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="radio"
                      name="timeline"
                      value={option.value}
                      checked={formData.timeline === option.value}
                      onChange={(e) => handleInputChange('timeline', e.target.value)}
                      style={{ display: 'none' }}
                    />
                    <div style={{ fontWeight: 600, color: '#111827' }}>{option.label}</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{option.description}</div>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Pain Points */}
            <div>
              <label style={styles.label}>What are your main concerns? (check all that apply)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {painPointOptions.map(point => (
                  <label 
                    key={point.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={formData.painPoints.includes(point.id)}
                      onChange={() => handlePainPointToggle(point.id)}
                      style={{ width: '18px', height: '18px', accentColor: '#1e3a8a' }}
                    />
                    <span style={{ color: '#111827' }}>{point.label}</span>
                  </label>
                ))}
              </div>
              
              {formData.painPoints.includes('other') && (
                <input
                  type="text"
                  value={formData.otherPainPoint}
                  onChange={(e) => handleInputChange('otherPainPoint', e.target.value)}
                  style={{ ...styles.input, marginTop: '0.75rem' }}
                  placeholder="Please specify..."
                  onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              )}
            </div>
          </div>

          {/* Section 4: Provider Introduction Request */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIcon}>
                <svg style={{ width: '24px', height: '24px', color: '#1e3a8a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 style={styles.sectionTitle}>Service Provider Introductions</h2>
                <p style={styles.sectionSubtitle}>Connect with providers that may benefit your plan</p>
              </div>
            </div>
            
            <p style={{ fontSize: '1rem', color: '#374151', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              Please let us know if you would like to be introduced to service providers that could save you money while maintaining or improving service quality.
            </p>
            
            {/* Yes/No Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={styles.label}>Would you like us to introduce you to potential service providers?</label>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {[
                  { value: true, label: 'Yes, please' },
                  { value: false, label: 'Not right now' }
                ].map(option => (
                  <label 
                    key={String(option.value)}
                    style={{
                      padding: '0.75rem 2rem',
                      border: `2px solid ${formData.wantsIntroduction === option.value ? '#1e3a8a' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: formData.wantsIntroduction === option.value ? 'rgba(30, 58, 138, 0.05)' : 'white',
                      fontWeight: 600,
                      color: '#111827',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="radio"
                      name="wantsIntroduction"
                      checked={formData.wantsIntroduction === option.value}
                      onChange={() => handleInputChange('wantsIntroduction', option.value)}
                      style={{ display: 'none' }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Contact Preferences */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <div>
                <label style={styles.label}>Preferred contact method:</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {[
                    { value: 'email', label: 'Email only' },
                    { value: 'phone', label: 'Phone preferred' },
                    { value: 'either', label: 'Either is fine' },
                  ].map(option => (
                    <label 
                      key={option.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem 1rem',
                        border: `2px solid ${formData.preferredContact === option.value ? '#1e3a8a' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: formData.preferredContact === option.value ? 'rgba(30, 58, 138, 0.05)' : 'white',
                        transition: 'all 0.2s'
                      }}
                    >
                      <input
                        type="radio"
                        name="preferredContact"
                        value={option.value}
                        checked={formData.preferredContact === option.value}
                        onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                        style={{ width: '16px', height: '16px', accentColor: '#1e3a8a' }}
                      />
                      <span style={{ color: '#111827' }}>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {formData.preferredContact !== 'email' && (
                <div>
                  <label style={styles.label}>Best time to call:</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {[
                      { value: 'morning', label: 'Morning (8am-12pm)' },
                      { value: 'afternoon', label: 'Afternoon (12pm-5pm)' },
                      { value: 'evening', label: 'Evening (5pm-8pm)' },
                    ].map(option => (
                      <label 
                        key={option.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          border: `2px solid ${formData.bestTimeToCall === option.value ? '#1e3a8a' : '#e5e7eb'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: formData.bestTimeToCall === option.value ? 'rgba(30, 58, 138, 0.05)' : 'white',
                          transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="radio"
                          name="bestTimeToCall"
                          value={option.value}
                          checked={formData.bestTimeToCall === option.value}
                          onChange={(e) => handleInputChange('bestTimeToCall', e.target.value)}
                          style={{ width: '16px', height: '16px', accentColor: '#1e3a8a' }}
                        />
                        <span style={{ color: '#111827' }}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 5: Consent & Submit */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionIcon}>
                <svg style={{ width: '24px', height: '24px', color: '#1e3a8a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 style={styles.sectionTitle}>Consent & Submit</h2>
                <p style={styles.sectionSubtitle}>Review and agree to continue</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {[
                { field: 'consentContact', label: 'I agree to be contacted about my 401(k) plan', required: true },
                { field: 'consentSharing', label: 'I consent to sharing my information with recommended providers for quotes', required: true },
                { field: 'consentTerms', label: <>I agree to the <a href="/terms" target="_blank" style={{ color: '#1e3a8a' }}>Terms of Service</a> and <a href="/privacy" target="_blank" style={{ color: '#1e3a8a' }}>Privacy Policy</a></>, required: true },
                { field: 'consentDisclosure', label: 'I understand this is educational information, not fiduciary investment advice', required: true },
              ].map((item) => (
                <label 
                  key={item.field}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '1rem',
                    border: `2px solid ${errors[item.field] ? '#ef4444' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: errors[item.field] ? '#fef2f2' : 'white'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData[item.field]}
                    onChange={(e) => handleCheckboxChange(item.field, e.target.checked)}
                    style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#1e3a8a' }}
                  />
                  <span style={{ color: '#111827' }}>
                    {item.label} {item.required && <span style={{ color: '#ef4444' }}>*</span>}
                  </span>
                </label>
              ))}
            </div>
            
            {/* Additional Comments */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={styles.label}>Additional comments or questions:</label>
              <textarea
                value={formData.additionalComments}
                onChange={(e) => handleInputChange('additionalComments', e.target.value)}
                rows="4"
                style={{
                  ...styles.input,
                  resize: 'none'
                }}
                placeholder="Tell us anything else we should know..."
                onFocus={(e) => e.target.style.borderColor = '#1e3a8a'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.primaryButton,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '1.125rem',
                padding: '1rem'
              }}
              onMouseEnter={(e) => !submitting && (e.target.style.background = styles.primaryBlueHover)}
              onMouseLeave={(e) => !submitting && (e.target.style.background = styles.primaryBlue)}
            >
              {submitting ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Submitting...
                </>
              ) : (
                <>🚀 Submit Request</>
              )}
            </button>
            
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', textAlign: 'center', marginTop: '1rem' }}>
              By submitting this form, we'll review your information and reach out within 2 business days.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlanSponsorIntake;
